import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { fetchPosts } from '../../../fetchProjects';

const API_BASE_URL = 'https://spaceportfolio-api.rajansudhan0.workers.dev';

const BlogManager = ({ adminKey }) => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filtering and Search State
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async (forceRefresh = false) => {
    setLoading(true);
    const data = await fetchPosts(forceRefresh, adminKey);
    setPosts(data);
    
    // Extract unique categories
    const uniqueCats = new Set(data.map(p => p.category).filter(Boolean));
    setCategories(Array.from(uniqueCats));
    
    setLoading(false);
  };

  // --- CRUD Operations ---

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setStatusMessage('');

    const formData = new FormData(e.target);
    
    // Helper to parse comma separated lists into arrays
    const parseList = (str) => str ? str.split(',').map(s => s.trim()).filter(Boolean) : [];

    const postData = {
      id: editingPost?.id || undefined,
      title: formData.get('title'),
      slug: formData.get('slug'),
      category: formData.get('category'),
      excerpt: formData.get('excerpt'),
      content: formData.get('content'),
      image: formData.get('image'),
      author: formData.get('author') || 'Mr. Sudhan R',
      date: formData.get('date'),
      readTime: formData.get('readTime'),
      tags: parseList(formData.get('tags')),
      published: formData.get('published') === 'on' ? 1 : 0,
      featured: formData.get('featured') === 'on' ? 1 : 0,
      views: isNaN(parseInt(formData.get('views'), 10)) ? 0 : parseInt(formData.get('views'), 10)
    };

    try {
      const res = await fetch(`${API_BASE_URL}/api/posts`, {
        method: editingPost ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Key': adminKey
        },
        body: JSON.stringify(postData)
      });

      if (!res.ok) throw new Error('Failed to save post');
      
      setStatusMessage({ type: 'success', text: 'Blog post saved successfully!' });
      loadPosts(true);
      
      setTimeout(() => {
        setIsModalOpen(false);
        setStatusMessage('');
      }, 1500);

    } catch (err) {
      console.error(err);
      setStatusMessage({ type: 'error', text: err.message });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/posts/${id}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Key': adminKey }
      });
      if (!res.ok) throw new Error('Failed to delete post');
      loadPosts(true);
    } catch (err) {
      alert(err.message);
    }
  };

  const openAddModal = () => {
    setEditingPost(null);
    setStatusMessage('');
    setIsModalOpen(true);
  };

  const openEditModal = (post) => {
    setEditingPost(post);
    setStatusMessage('');
    setIsModalOpen(true);
  };

  // --- Filtering Logic ---
  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          post.excerpt?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCat = selectedCategory === 'All Categories' || post.category === selectedCategory;
    return matchesSearch && matchesCat;
  });

  if (loading) return <div className="h-64 bg-white/5 rounded-2xl animate-pulse"></div>;

  return (
    <div className="space-y-6 pb-12">
      
      {/* Top Action Bar */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between mb-8">
        
        {/* Search & Filter */}
        <div className="flex w-full sm:w-auto gap-4">
          <div className="relative w-full sm:w-64">
            <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input 
              type="text"
              placeholder="Search posts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-surface/50 border border-white/10 rounded-xl py-2.5 pl-10 pr-4 text-text-primary focus:outline-none focus:border-accent/50 focus:ring-1 focus:ring-accent/50 transition-smooth"
            />
          </div>

          <div className="relative w-full sm:w-48">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full bg-surface/50 border border-white/10 rounded-xl py-2.5 px-4 text-text-primary appearance-none focus:outline-none focus:border-accent/50 focus:ring-1 focus:ring-accent/50 transition-smooth cursor-pointer"
            >
              <option value="All Categories">All Categories</option>
              {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
            <Icon name="ChevronDown" size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center w-full sm:w-auto gap-3">
          <button 
            onClick={openAddModal}
            className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-accent hover:bg-accent-600 text-white px-5 py-2.5 rounded-xl font-semibold transition-smooth shadow-lg hover:shadow-accent/25"
          >
            <Icon name="Plus" size={18} />
            Write Post
          </button>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-surface/50 border border-white/10 rounded-2xl overflow-x-auto shadow-2xl">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-white/10 text-sm font-semibold text-text-muted">
              <th className="p-4 w-12 text-center">
                <input type="checkbox" className="rounded-sm bg-white/10 border-white/20 text-accent focus:ring-accent/50" />
              </th>
              <th className="p-4">Title</th>
              <th className="p-4">Category</th>
              <th className="p-4">Date</th>
              <th className="p-4 text-center">Featured</th>
              <th className="p-4 text-center">Status</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="text-text-primary text-sm divide-y divide-white/5">
            {filteredPosts.length === 0 ? (
               <tr><td colSpan="6" className="p-8 text-center text-text-muted">No posts found.</td></tr>
            ) : (
                filteredPosts.map((post) => (
                <tr key={post.id} className="hover:bg-white/[0.02] transition-colors group">
                    <td className="p-4 w-12 text-center">
                      <input type="checkbox" className="rounded-sm bg-white/10 border-white/20 text-accent focus:ring-accent/50" />
                    </td>
                    <td className="p-4 font-medium">
                      {post.title}
                    </td>
                    <td className="p-4">
                      <span className="bg-white/5 border border-white/10 px-2.5 py-1 rounded-md text-xs text-text-secondary whitespace-nowrap">
                          {post.category || 'N/A'}
                      </span>
                    </td>
                    <td className="p-4 text-text-secondary whitespace-nowrap">
                        {post.date || 'N/A'}
                    </td>
                    <td className="p-4 text-center">
                      {post.featured === 1 ? (
                        <div className="flex items-center justify-center text-warning" title="Featured">
                          <Icon name="Star" size={16} className="fill-current" />
                        </div>
                      ) : (
                        <span className="text-text-muted text-xs">-</span>
                      )}
                    </td>
                    <td className="p-4 text-center">
                      {post.published === 1 ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-success/10 text-success border border-success/20">
                          <div className="w-1.5 h-1.5 rounded-full bg-success"></div>
                          Published
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-warning/10 text-warning border border-warning/20">
                          <div className="w-1.5 h-1.5 rounded-full bg-warning"></div>
                          Draft
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-2 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                        <button 
                            onClick={() => openEditModal(post)}
                            className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 text-text-secondary hover:text-white flex items-center justify-center transition-smooth"
                            title="Edit"
                        >
                            <Icon name="Edit2" size={14} />
                        </button>
                        <button 
                            onClick={() => handleDelete(post.id)}
                            className="w-8 h-8 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 flex items-center justify-center transition-smooth border border-transparent hover:border-red-500/30"
                            title="Delete"
                        >
                            <Icon name="Trash2" size={14} />
                        </button>
                    </div>
                    </td>
                </tr>
                ))
            )}
          </tbody>
        </table>
      </div>

       {/* Blog Form Modal */}
       {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 pt-20 pb-4">
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={() => !isSaving && setIsModalOpen(false)}></div>
          <div className="bg-surface border border-white/10 rounded-2xl w-full max-w-4xl max-h-[85vh] overflow-y-auto relative z-10 shadow-3xl">
            <div className="sticky top-0 bg-surface/90 backdrop-blur-md px-6 py-4 border-b border-white/10 flex justify-between items-center z-20">
              <h3 className="font-heading font-bold text-xl text-text-primary">
                {editingPost ? 'Edit Post' : 'New Blog Post'}
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="text-text-muted hover:text-white transition-smooth">
                <Icon name="X" size={24} />
              </button>
            </div>

            <form onSubmit={handleSave} className="p-6 space-y-8">
              {/* Basic Info */}
              <div className="space-y-4">
                <h4 className="font-heading font-semibold text-accent text-sm uppercase tracking-wider">Article Content</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Title *</label>
                    <input type="text" name="title" required defaultValue={editingPost?.title} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Slug (URL Part) *</label>
                    <input type="text" name="slug" required defaultValue={editingPost?.slug} placeholder="my-awesome-post" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Category *</label>
                    <input type="text" name="category" required defaultValue={editingPost?.category} placeholder="Technology, Design..." className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Read Time</label>
                    <input type="text" name="readTime" defaultValue={editingPost?.readTime} placeholder="5 min read" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                  </div>
                </div>
                <div>
                   <label className="block text-sm font-medium text-text-secondary mb-1">Views Count</label>
                   <input type="number" name="views" defaultValue={editingPost?.views || 0} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">Excerpt (Short Summary) *</label>
                  <textarea name="excerpt" required rows={2} defaultValue={editingPost?.excerpt} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent resize-none"></textarea>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">Main Content (HTML/Rich Text)</label>
                  <textarea name="content" rows={10} defaultValue={editingPost?.content} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent font-mono text-sm"></textarea>
                </div>
              </div>

              {/* Media & Metadata */}
              <div className="space-y-4 pt-4 border-t border-white/10">
                <h4 className="font-heading font-semibold text-accent text-sm uppercase tracking-wider">Media & Metadata</h4>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">Cover Image URL</label>
                  <input type="text" name="image" defaultValue={editingPost?.image} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Author Name</label>
                    <input type="text" name="author" defaultValue={editingPost?.author || 'Mr. Sudhan R'} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Publish Date</label>
                    <input type="text" name="date" defaultValue={editingPost?.date || new Date().toISOString().split('T')[0]} placeholder="YYYY-MM-DD" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">Tags (Comma separated)</label>
                  <input type="text" name="tags" defaultValue={Array.isArray(editingPost?.tags) ? editingPost.tags.join(', ') : ''} placeholder="React, Career, Insights..." className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t border-white/10">
                <h4 className="font-heading font-semibold text-accent text-sm uppercase tracking-wider">Visibility</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center">
                      <label className="relative inline-flex items-center cursor-pointer">
                          <input type="checkbox" name="published" defaultChecked={editingPost?.published === 1} className="sr-only peer" />
                          <div className="w-11 h-6 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-white/50 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-success ring-1 ring-white/10"></div>
                          <span className="ml-3 text-sm font-medium text-text-secondary">Publish immediately</span>
                      </label>
                  </div>
                  <div className="flex items-center">
                      <label className="relative inline-flex items-center cursor-pointer">
                          <input type="checkbox" name="featured" defaultChecked={editingPost?.featured === 1} className="sr-only peer" />
                          <div className="w-11 h-6 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-white/50 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-warning ring-1 ring-white/10"></div>
                          <span className="ml-3 text-sm font-medium text-text-secondary">Mark as Featured</span>
                      </label>
                  </div>
                </div>
              </div>

              {/* Status & Submit */}
              <div className="flex items-center justify-between pt-6 mt-6 border-t border-white/10 sticky bottom-0 bg-surface/90 backdrop-blur-md p-4 -mx-6 -mb-6 rounded-b-2xl z-20">
                <div className="text-sm font-medium">
                  {statusMessage && (
                    <span className={statusMessage.type === 'success' ? 'text-success' : 'text-error'}>
                      {statusMessage.text}
                    </span>
                  )}
                </div>
                <div className="flex gap-3">
                  <button type="button" onClick={() => setIsModalOpen(false)} className="px-5 py-2.5 rounded-xl text-text-primary bg-white/5 hover:bg-white/10 transition-smooth font-medium">
                    Cancel
                  </button>
                  <button type="submit" disabled={isSaving} className="px-5 py-2.5 rounded-xl text-white bg-accent hover:bg-accent-600 disabled:opacity-50 transition-smooth font-semibold flex items-center gap-2">
                    {isSaving ? (
                      <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> Saving...</>
                    ) : (
                      <><Icon name="Save" size={18} /> Save Article</>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default BlogManager;
